/* eslint-disable @typescript-eslint/no-empty-object-type */
import {
  ComponentContext, ChildComponentContext,
  ComponentDescriptor
} from 'app/infrastructure/ui/types-uischema';
import { debug, error } from 'app/infrastructure/log';
import { LoadOptions } from 'app/types-shared';
import { mapToOptionsRequestParams, mapToOptionsRequestContext } from 'app/infrastructure/data/codes';
import { AddDocumentItemOptions, DeleteDocumentItemOptions } from 'app/infrastructure/ui/form/types';
import { contextToStorePath } from 'app/infrastructure/ui/renderer/resolvers';
import { getEntityTypeFromPath } from 'app/infrastructure/store/entity';
import isPlainObject from 'lodash/isplainobject';
import { COMPONENT_ACTIONS } from 'app/infrastructure/ui/builder/component-actions';

interface EventToActionType {
  [event: string]: string;
}

interface BoundActionCreatorMap {
  [name: string]: (context: Object, ...args: any[]) => void;
}

/**
 * What actionCreator to fire for a component event that was defined as prop
 */
const actionForEvent: EventToActionType = {
  onChange: 'valueChanged',
  onBlur: 'componentLostFocus',
  onFocus: 'componentHasFocus',
  onLoadOptions: 'loadOptions',
  onLoadCategoryOptions: 'loadCategoryOptions',
  onEditDocument: 'editDocument',
  onDeleteDocument: 'deleteDocument',
  onDuplicateDocument: 'duplicateDocument',
  onAddDocumentItem: 'addDocumentItem',
  onCopyDocumentItem: 'copyDocumentItem',
  onDeleteDocumentItem: 'deleteDocumentItem',
  onEditDocumentItem: 'editDocumentItem',
  onSubmitDocument: 'onSubmit',
  onCloseDocument: 'onCancel',
  getDefaultValue: 'getDefaultValue',
  onSelectDocumentItem: 'selectDocumentItem',
  onLoadBowtie: 'loadBowtie',
  onLoadPermissions: 'loadPermissions'
};
// eslint:disable max-func-body-length

/**
 * Dynamic creation of callbacks and functions for component events
 *
 * @param dispatch: function - the redux store dispatch function to fire action creators
 * @returns Object map describing the different events callbacks
 */
export const eventBuilder = (actionCreators: BoundActionCreatorMap) => {

  // return a function that when called returns the function for the given
  // component event
  return (
    context: ComponentContext | ChildComponentContext,
    descriptor: ComponentDescriptor,
    componentEvent: string
  ) => {

    const actions: any = {
      /**
       * The callback function that fires a redux action when components value changes
       *
       */
      onChange: (valueArg: any) => {

        try {
          let finalValue = [];

          // eventType is the redux action creator to run e.g. onChange, onBlur,
          const eventType = actionForEvent[componentEvent];
          const action: Function = actionCreators[eventType];

          debug('dispatching', eventType, context, descriptor, valueArg);

          // some components passes simple values, some passes objects, some passes arrays of objects
          const isValue = (x: any) => !Object.is(x, undefined) && !Object.is(x, null);
          const isValidValueObject = isPlainObject(valueArg) && isValue(valueArg.value);
          const isValidValueArray = Array.isArray(valueArg) && valueArg.every(isValue);
          const isValidPlainValue = !isPlainObject(valueArg) && !isValidValueObject && !isValidValueArray;

          if (isValidValueObject) {
            finalValue = [valueArg];
          }

          if (isValidValueArray) {
            finalValue = valueArg;
          }

          if (isValidPlainValue && isValue(valueArg)) {
            finalValue = [{ value: valueArg }];
          }

          const immediateSave = descriptor.properties && descriptor.properties.immediateSaveOnValueChange;

          action(contextToStorePath(descriptor, context), descriptor.dataTag, finalValue, immediateSave);
        } catch (err) {
          error('Error in onChange', err);
        }
      },
      /**
       * When a child entity is selected in the UI, e.g a Card or Row
       */
      onSelectDocumentItem: (itemKey?: any) => {
        try {
          debug('onSelectDocumentItem', itemKey);
          const eventType = actionForEvent[componentEvent];
          const action = actionCreators[eventType];

          // Optionally de-selecting by providing explicit null
          action && action( itemKey === null ? undefined : (itemKey || context.path));

        } catch (err) {
          error('Error in onSelectDocumentItem', err);
        }
      },
      /**
       * When a child entity should be edited
       */
      onEditDocumentItem: () => {
        try {
          const eventType = actionForEvent[componentEvent];
          const actionCreator: Function = actionCreators[eventType];

          actionCreator(context.path);
        } catch (err) {
          error('Error in onEditDocumentItem', err);
        }
      },
      /**
       * Deletes a child entity from a Case
       */
      onDeleteDocumentItem: (
          selectParentItem: boolean = true,
          options: DeleteDocumentItemOptions = {}
        ) => {

        try {
          debug('onDeleteDocumentItem');
          const eventType = actionForEvent[componentEvent];
          const action = actionCreators[eventType];

          const pathFromOptions = options.parentEntityPath && options.entityType && options.childId;
          const entityPath = pathFromOptions ?
            [...options.parentEntityPath, { [options.entityType]: options.childId }] :
            contextToStorePath(descriptor, context);

          action && action(selectParentItem, entityPath);

        } catch (err) {
          error('Error in onDeleteDocumentItem', err);
        }
      },
      onCopyDocumentItem: (noOfCopies: number) => {

        try {
          debug('onCopyDocumentItem');
          const eventType = actionForEvent[componentEvent];
          const action = actionCreators[eventType];

          action && action(contextToStorePath(descriptor, context), {noOfCopies});

        } catch (err) {
          error('Error in onCopyDocumentItem', err);
        }
      },
      /**
       * Adds an child entity to the Case
       */
      onAddDocumentItem: (
          selectAddedItem: boolean = true,
          options: AddDocumentItemOptions = {}
        ) => {
          try {

            debug('onAddDocumentItem', options);

            const actionCreatorName = actionForEvent[componentEvent];
            const action = actionCreators[actionCreatorName];

            const entityType = options.entityType || getEntityTypeFromPath(descriptor.entityTypePath);
            const entityTypePathMatchesContextPath = 
              descriptor.entityTypePath && descriptor.entityTypePath.every(p => context.path.some(c => c[p]));

            // Because we do not support nesting a table in itself, we need to remove ancestors that share this type.
            const parentPath = options.parentEntityPath || contextToStorePath(descriptor, context).filter(p => !p[entityType]);

            // If this is an iterator trying to add, it should use the default values in its descriptor.
            // In other cases it should use the context default values, if available.
            let defaultValues = descriptor.iterate && descriptor.iterate.defaultValues;
            if (!defaultValues && (context as ChildComponentContext).defaultValues && entityTypePathMatchesContextPath) {
              defaultValues = (context as ChildComponentContext).defaultValues;
            }

            action && action(parentPath, entityType, selectAddedItem, {
              ...options,
              defaultValues
            });

          } catch (err) {
            error('Error in onAddDocumentItem', err);
          }
      },
      /**
       * The callback for when a component requests a code table lookup or search
       */
      onLoadOptions: (loadOptions?: LoadOptions) => {
        try {

          // eventType is the redux action creator to run e.g. onChange, onBlur,etc
          const eventType = actionForEvent[componentEvent];

          debug('dispatching', eventType, context, descriptor.codetable, loadOptions);

          const actionCreator: Function = actionCreators[eventType];

          actionCreator(
            mapToOptionsRequestContext(context, descriptor, loadOptions),
            mapToOptionsRequestParams(loadOptions)
          );

        } catch (err) {
          error('Error in onLoadOptions', err);
        }
      },
      /**
       * The callback for when a component requests category options
       */
      onLoadCategoryOptions: (loadCategoryOptions?: LoadOptions) => {
          try {
  
            // eventType is the redux action creator to run e.g. onChange, onBlur,etc
            const eventType = actionForEvent[componentEvent];
  
            debug('dispatching', eventType, context, descriptor.codetable, loadCategoryOptions);
  
            const actionCreator: Function = actionCreators[eventType];
  
            actionCreator(
              mapToOptionsRequestContext(context, descriptor, loadCategoryOptions),
              mapToOptionsRequestParams(loadCategoryOptions)
            );
  
          } catch (err) {
            error('Error in onLoadCategoryOptions', err);
          }
        },
      /**
       * Redirect to draft edit route
       */
      onEditDocument: () => {
        try {
          const eventType = actionForEvent[componentEvent];
          const actionCreator: Function = actionCreators[eventType];
          actionCreator();
        } catch (err) {
          error('Error in onEditDocument', err);
        }
      },
      /**
       * Fire a delete api request
       */
      onDeleteDocument: () => {
        try {
          const eventType = actionForEvent[componentEvent];
          const actionCreator: Function = actionCreators[eventType];
          actionCreator();
        } catch (err) {
          error('Error in onDeleteDocument', err);
        }
      },
      /**
       * Redirect to the case duplicate route
       */
      onDuplicateDocument: () => {
        try {
          const eventType = actionForEvent[componentEvent];
          const actionCreator: Function = actionCreators[eventType];
          actionCreator();
      } catch (err) {
          error('Error in onDuplicateDocument', err);
        }
      },
      onSubmitDocument: (continueEditing: boolean = false) => {
        try {
          const eventType = actionForEvent[componentEvent];
          const actionCreator: Function = actionCreators[eventType];
          actionCreator(continueEditing);
        } catch (err) {
          error('Error submitting document', err);
        }
      },
      onCloseDocument: () => {
        try {

          const eventType = actionForEvent[componentEvent];
          const actionCreator: Function = actionCreators[eventType];
          actionCreator();
        } catch (err) {
          error('Error closing document', err);
        }
      },
      onLoadPermissions: () => {
        try {
          const eventType = actionForEvent[componentEvent];
          const actionCreator: Function = actionCreators[eventType];

          actionCreator();
        } catch (err) {
          error('Error loading permissions', err);
        }
      },
      /**
       * Redirect to bowtie
       */
      onLoadBowtie: () => {
        try {
          const eventType = actionForEvent[componentEvent];
          const actionCreator: Function = actionCreators[eventType];
          actionCreator();
        } catch (err) {
          error('Error in onLoadBowtie', err);
        }
      }
    };

    return actions[componentEvent];

  };
};

/**
 * Build events for the described component type, based on included events in the `componentEvents` array
 *
 * @param actions actionCreators to be called from the predefined actions
 * @param descriptor ComponentDescriptor for the component to connect
 * @param resolveContext Execution context to create a closure for the callback
 */
export const buildEvents = (
  actions: {[action: string] : () => void},
  descriptor: ComponentDescriptor,
  resolveContext: ComponentContext | ChildComponentContext
) => {

  const eventMap: any = {};
  const createEvent = eventBuilder(actions);
  descriptor.componentEvents.filter(Boolean).forEach((eventName: string) =>
  {
    const event = createEvent(resolveContext, descriptor, eventName);
    const childContext = resolveContext as ChildComponentContext;

    if (childContext.virtual && eventName === COMPONENT_ACTIONS.onChange) {
      // Components in a virtual context must add themselves before changes can be made.
      const addEvent = createEvent(resolveContext, descriptor, COMPONENT_ACTIONS.onAddDocumentItem);
      eventMap[COMPONENT_ACTIONS.onChange] = (...args: any[]) => {

        // Add item.
        addEvent(false,
          { childId: childContext.primaryKey && childContext.primaryKey.value }
        );

        // Call onChange on newly created item.
        event(...args);
      };
    } else {
      eventMap[eventName] = event;
    }

  });

   return eventMap;
};
